import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { EnsuredSidekickConversation } from '../contexts/SidekickApiContext';
import {
  addMessage,
  markQuestionRetried,
  selectConversation,
  selectConversationId,
  selectConversationScopeVersion,
  selectIsStreaming,
  selectMessages,
  setAnsweringQuestion,
  setConversation,
} from '../state/aiSidekickChatSlice';
import type { TransportRequestContext } from '../state/chatTypes';
import {
  buildConversationTransportTurn,
  buildConversationView,
  buildCreatedConversation,
  buildUserMessage,
} from './useConversation.helpers';
import type {
  RetrySubmissionContext,
  UseConversationParams,
  UseConversationResult,
} from './useConversation.types';
import { useConversationHydration } from './useConversationHydration';
import { useConversationRetry } from './useConversationRetry';
import { useConversationScope } from './useConversationScope';
import { useConversationSubmissionLock } from './useConversationSubmissionLock';
import { useConversationTransport } from './useConversationTransport';
import { useSidekickApi } from './useSidekickApi';
import { useStopResponse } from './useStopResponse';

export { MAX_TRANSPORT_ATTEMPTS } from './useConversation.transport.helpers';
export type {
  UseConversationParams,
  UseConversationResult,
} from './useConversation.types';

/**
 * Orchestrates Sidekick conversation state, transport, and original-turn retries.
 * @param params - Optional network-error handler
 * @returns Conversation state and controls
 */
export function useConversation(
  params: UseConversationParams = {}
): UseConversationResult {
  const dispatch = useDispatch();
  const { apiBaseUrl, fleetId, interfaceName, ensureConversation } =
    useSidekickApi();
  const conversationFromStore = useSelector(selectConversation);
  const messagesFromStore = useSelector(selectMessages);
  const conversationIdFromStore = useSelector(selectConversationId);
  const conversationScopeVersion = useSelector(selectConversationScopeVersion);
  const isStreaming = useSelector(selectIsStreaming);

  const messagesRef = useRef(messagesFromStore);
  messagesRef.current = messagesFromStore;

  const conversation = useMemo(
    () => buildConversationView(conversationFromStore, messagesFromStore),
    [conversationFromStore, messagesFromStore]
  );

  const activeConversationId = conversationIdFromStore;
  const { captureScope, isScopeCurrent } = useConversationScope(
    activeConversationId,
    conversationScopeVersion
  );
  const {
    cancelPendingSubmission,
    isSubmissionCancelled,
    releaseSubmission,
    reserveSubmission,
    restoreSubmission,
  } = useConversationSubmissionLock(
    activeConversationId,
    conversationScopeVersion
  );

  useConversationHydration();

  const {
    activateRetry,
    beginSubmission,
    cancelRetryReservation,
    cancelSubmission,
    notifyNetworkError,
    reserveRetry,
    runTurn,
    stopTurn,
  } = useConversationTransport({
    activeConversationId,
    apiBaseUrl,
    onNetworkError: params.onNetworkError,
  });

  const { stopResponse, isStopPending } = useStopResponse(
    stopTurn,
    cancelPendingSubmission
  );

  const sendMessage = useCallback(
    (
      content: string,
      requestContext?: TransportRequestContext,
      retryContext?: RetrySubmissionContext
    ): boolean => {
      const trimmed = content.trim();
      if (trimmed.length === 0 || isStreaming || isStopPending) {
        return false;
      }
      if (apiBaseUrl === '') {
        if (import.meta.env.DEV) {
          console.warn(
            'Sidekick orchestrator: apiBaseUrl is empty. Pass it on <SidekickLayoutShell apiBaseUrl=... /> to enable transport.'
          );
        }
        return false;
      }

      const submissionId = reserveSubmission();
      if (submissionId === null) {
        return false;
      }
      const submissionScope = captureScope();
      beginSubmission(retryContext?.errorId ?? null);

      void (async () => {
        let targetConversationId = activeConversationId;
        let ensuredConversation: EnsuredSidekickConversation | null = null;
        if (targetConversationId === null) {
          if (ensureConversation === undefined) {
            if (import.meta.env.DEV) {
              console.warn(
                'Sidekick orchestrator: cannot send before a real conversation id is available.'
              );
            }
            cancelSubmission();
            return;
          }
          try {
            ensuredConversation = await ensureConversation();
            targetConversationId = ensuredConversation.conversationId;
          } catch (error) {
            if (!isScopeCurrent(submissionScope)) {
              return;
            }
            cancelSubmission();
            notifyNetworkError();
            if (import.meta.env.DEV) {
              console.warn(
                '[useConversation] failed to create conversation before send',
                error
              );
            }
            return;
          }
        }
        if (!isScopeCurrent(submissionScope, targetConversationId)) {
          return;
        }
        // Stopped while its conversation was created: it stays in the chat
        // like any stopped question, but is never sent.
        const wasStopped = isSubmissionCancelled(submissionId);
        if (!wasStopped && !restoreSubmission(submissionId)) {
          return;
        }
        if (activeConversationId === null && ensuredConversation !== null) {
          dispatch(
            setConversation(
              buildCreatedConversation(ensuredConversation, messagesRef.current)
            )
          );
          // Restore submission state after adopting the matching created id.
          if (!wasStopped) {
            beginSubmission(retryContext?.errorId ?? null);
          }
        }

        const transportTurn = buildConversationTransportTurn(
          trimmed,
          targetConversationId,
          requestContext,
          fleetId,
          interfaceName
        );
        let sourceMessageId = retryContext?.sourceMessageId;
        if (sourceMessageId === undefined) {
          const userMessage = buildUserMessage(targetConversationId, trimmed);
          sourceMessageId = userMessage.id;
          dispatch(addMessage(userMessage));
        }

        if (wasStopped) {
          return;
        }
        const retryErrorId = retryContext?.errorId ?? null;
        if (retryContext !== undefined) {
          dispatch(markQuestionRetried(sourceMessageId));
        }
        dispatch(setAnsweringQuestion(sourceMessageId));
        await runTurn(sourceMessageId, transportTurn, retryErrorId);
      })().finally(() => {
        releaseSubmission(submissionId);
      });
      return true;
    },
    [
      activeConversationId,
      apiBaseUrl,
      beginSubmission,
      cancelSubmission,
      captureScope,
      dispatch,
      ensureConversation,
      fleetId,
      interfaceName,
      isScopeCurrent,
      isStopPending,
      isStreaming,
      isSubmissionCancelled,
      notifyNetworkError,
      releaseSubmission,
      reserveSubmission,
      restoreSubmission,
      runTurn,
    ]
  );

  const retry = useConversationRetry({
    sendMessage,
    reserveRetry,
    cancelRetryReservation,
    activateRetry,
  });

  return {
    conversation,
    displayMessages: messagesFromStore,
    sendMessage,
    retry,
    stopResponse,
    isStreaming,
    isStopPending,
  };
}
